// The horizontal twin of the rail: whatever this person keeps within reach,
// with its name beside it.
//
// Only a row of links. What goes on it is chosen in `FavouritesMenu`, which
// lives beside the plan chip at the other end of the header rather than at the
// end of this row — this row scrolls when it is full, and the control that
// empties it must not be the thing that scrolls out of reach.

import { NavLink, useLocation } from "react-router-dom";
import { AccountMark } from "../components/AccountMark";
import type { FavouriteItem } from "./state";

export function TopFavoritesBar({
  favourites, loading,
}: {
  favourites: FavouriteItem[];
  loading: boolean;
}) {
  const location = useLocation();
  const composingTo = location.pathname === "/compose"
    ? new URLSearchParams(location.search).get("to")
    : null;

  /** On this screen, or composing to this account. */
  const isCurrent = (item: FavouriteItem) =>
    item.connection
      ? composingTo === item.connection.id
      : location.pathname === item.route;

  return (
    <div className="topbar-favs">
      <div className="fav-pill-row">
        {favourites.map((item) => {
          const Icon = item.icon;
          const on = isCurrent(item);
          return (
            <NavLink
              key={item.key}
              to={item.route}
              className={on ? "fav-pill on" : "fav-pill"}
              aria-current={on ? "page" : undefined}
              // For a screen this is the disclosure `nav.ts` requires; for an
              // account it names the platform the composer would post to.
              title={item.title ?? `${item.hint} — ${item.label}`}
            >
              {item.connection
                ? <AccountMark connection={item.connection} />
                : Icon ? <Icon className="nav-icon" /> : null}
              <span className="fav-pill-name">{item.label}</span>
            </NavLink>
          );
        })}

        {/* Not "you have no favourites": the row is empty here only while the
            accounts that fill it by default are still being read. */}
        {favourites.length === 0 && loading && (
          <span className="fav-pill-skeleton" aria-hidden="true" />
        )}
        {favourites.length === 0 && !loading && (
          <span className="muted fav-pill-none">
            Nothing pinned — choose with the ★ on the right
          </span>
        )}
      </div>
    </div>
  );
}
